// ============================================
// Three.js Scene Management
// Core scene, camera, and renderer handling
// ============================================

import type { Scene, OrthographicCamera, WebGLRenderer } from './types';
import { CONFIG } from '../../../config/game-config';

// Three.js core objects
let threeScene: Scene | null = null;
let threeCamera: OrthographicCamera | null = null;
let threeRenderer: WebGLRenderer | null = null;
let threeInitialized = false;

/**
 * Check if Three.js is initialized
 */
export function getThreeInitialized(): boolean {
    return threeInitialized;
}

/**
 * Get the Three.js renderer
 */
export function getThreeRenderer(): WebGLRenderer | null {
    return threeRenderer;
}

/**
 * Get the Three.js scene
 */
export function getThreeScene(): Scene | null {
    return threeScene;
}

/**
 * Get the Three.js camera
 */
export function getThreeCamera(): OrthographicCamera | null {
    return threeCamera;
}

/**
 * Initialize Three.js scene, camera, and renderer
 */
export function initThreeScene(): void {
    // Create scene
    threeScene = new THREE.Scene();

    // Create orthographic camera (for 2D game) - full canvas width
    threeCamera = new THREE.OrthographicCamera(
        -CONFIG.layout.canvasWidth / 2, CONFIG.layout.canvasWidth / 2,
        CONFIG.height / 2, -CONFIG.height / 2,
        0.1, 2000
    );
    threeCamera.position.z = 1000;

    // Create renderer - full canvas width
    threeRenderer = new THREE.WebGLRenderer({
        alpha: false,
        antialias: true,
        preserveDrawingBuffer: true
    });
    threeRenderer.setSize(CONFIG.layout.canvasWidth, CONFIG.height);
    threeRenderer.setPixelRatio(1);

    // Add to container
    const container = document.getElementById('three-canvas-container');
    if (container) {
        container.appendChild(threeRenderer.domElement);
    } else {
        console.warn('three-canvas-container not found, Three.js canvas not added to DOM');
    }

    threeInitialized = true;
}

/**
 * Render the Three.js scene
 */
export function renderThreeScene(): void {
    if (threeRenderer && threeScene && threeCamera) {
        threeRenderer.render(threeScene, threeCamera);
    }
}

/**
 * Dispose of Three.js resources
 */
export function disposeThreeScene(): void {
    if (threeRenderer) {
        threeRenderer.dispose();

        // Remove canvas from DOM
        if (threeRenderer.domElement.parentElement) {
            threeRenderer.domElement.parentElement.removeChild(threeRenderer.domElement);
        }
        threeRenderer = null;
    }

    threeScene = null;
    threeCamera = null;
    threeInitialized = false;
}

/**
 * Add an object to the scene
 */
export function addToScene(object: Parameters<Scene['add']>[0]): void {
    if (threeScene) {
        threeScene.add(object);
    }
}

/**
 * Remove an object from the scene
 */
export function removeFromScene(object: Parameters<Scene['remove']>[0]): void {
    if (threeScene) {
        threeScene.remove(object);
    }
}
